import { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Icon from '../../../atoms/icon';
import InteractiveElement from '../../../atoms/interactive-element';
import ButtonIconLink from '../../../atoms/button-icon-link';
import ScreenerAppContext from '../../../../stores/screener/screener-app-context';
import { connect } from 'react-redux';
import { urls } from '../../../../utils/url-helper/index';
import { usePlanNoticeUrl } from '../../../../hooks/api';

import DropdownBasic from '../../../atoms/dropdown-basic';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import styles from './plan-name.module.scss';
import { CHANNELS } from '../../../../shared/screener-constants';
import {
  updatePlanHoldings,
  restoreParticipantHoldings
} from '../../../../stores/screener/actions/filter-actions';

const className = augmentClassName(styles);

function handleLinkClick(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export const PlanName = ({
  plans,
  updateScreenerPlanHoldings,
  participantHoldings,
  restorePlanHoldings
}) => {
  const { channel, yyy, isMobile } = useContext(ScreenerAppContext);
  const isPublic = channel === CHANNELS.PUBLIC;
  const isMicroSite = channel === CHANNELS.DYNAMICMICROSITES;
  const isMyTiaa = channel === CHANNELS.MYTIAA;
  const [showPlanName, setShowPlanName] = useState(isPublic || isMyTiaa || isMicroSite);
  const [showPlanDetail, setShowPlanDetail] = useState(false);
  const chevron = showPlanName ? (
    <Icon.ChevronUp {...className('pl-2', 'ml-4', 'stroke')} height="15px" width="18px" />
  ) : (
    <Icon.ChevronDown {...className('pl-2', 'ml-4', 'stroke')} height="15px" width="18px" />
  );
  const planNumbers = plans.map(plan => plan.number);
  const noticeUrls = usePlanNoticeUrl({ planNumbers, channel })?.data || [];

  const searchPlanDetail = (planNumber, subPlan) => {
    if (isPublic) return;
    setShowPlanDetail(true);
    updateScreenerPlanHoldings(planNumber, subPlan, channel, yyy);
  };

  const showAllPlans = () => {
    setShowPlanDetail(false);
    restorePlanHoldings(participantHoldings);
  };

  if (!plans.length) {
    return <></>;
  }
  return (
    <Container>
      {!isPublic &&
        (showPlanDetail ? (
          <InteractiveElement
            as="Row"
            {...className('align-items-center', 'm-0')}
            onClick={() => showAllPlans()}
          >
            <Icon.ArrowLeft height="24px" width="24px" {...className('cursor-pointer')} />
            <div {...className('plan', 'ml-3', 'mt-1', isMobile ? 'px-4' : null)}>
              {isMicroSite ? 'View plans offered' : 'View all my plans'}
            </div>
          </InteractiveElement>
        ) : (
          <InteractiveElement
            as="Row"
            {...className('align-items-center', 'm-0')}
            onClick={() => setShowPlanName(!showPlanName)}
          >
            <div {...className('plan', isMobile ? 'px-4' : null)}>
              {isMicroSite ? 'All retirement plans' : 'All of my retirement plans and IRAs'}
            </div>
            {chevron}
          </InteractiveElement>
        ))}
      {showPlanName && (
        <Row>
          <Col>
            {plans.map((plan, index) => {
              const qiuPdfUrl = urls.plans.getQiuPdfUrl(plan.number);
              let planNoticeUrl = null;
              let useRightMargin = false;
              noticeUrls.forEach(url => {
                if (url.includes(plan.number)) {
                  planNoticeUrl = url;
                  useRightMargin = true;
                }
              });
              const dropdownLinks = ['quarterlyPerformance'];
              if (planNoticeUrl) dropdownLinks.unshift('planInvestment');
              return (
                <div
                  data-key={`${plan.number || ''}-${plan.subPlanId || ''}-${index}`}
                  key={`${plan.number || ''}-${plan.subPlanId || ''}-${index}`}
                  {...className(
                    'd-flex',
                    'justify-content-between',
                    'mt-6',
                    'screener-plan-quarterly-performance-row'
                  )}
                >
                  <div
                    {...className(
                      isMobile ? 'px-4' : null,
                      'screener-plan-name',
                      'plan',
                      isPublic && plans.length === 1 && 'cursor-text'
                    )}
                  >
                    <InteractiveElement
                      as="span"
                      {...className(!isPublic && !showPlanDetail ? 'underline' : '')}
                      onClick={() => searchPlanDetail(plan.number, plan.subPlanId)}
                    >
                      {plan.name}
                    </InteractiveElement>{' '}
                    | {plan.number}
                    {!isPublic && plan.subPlanId !== '' && <span> | {plan.subPlanId}</span>}
                  </div>
                  {isMobile ? (
                    <DropdownBasic items={dropdownLinks}>
                      <DropdownBasic.Trigger>
                        <Icon.Kebab style={{ fontSize: '14px' }} />
                      </DropdownBasic.Trigger>
                      <DropdownBasic.Item
                        {...className('position-relative')}
                        style={{ display: 'inline-flex', width: '100%' }}
                      >
                        {item => {
                          return item === 'planInvestment' ? (
                            <ButtonIconLink
                              text="Plan &amp; Investment Notice"
                              onClick={() => handleLinkClick(planNoticeUrl)}
                              variant="disclosureScreener"
                              textClass={'btn-icon'}
                              iconStyle={{
                                fontSize: '21px',
                                color: 'black',
                                'margin-right': '5px',
                                top: '0'
                              }}
                              buttonClasses={[
                                'flex-nowrap',
                                'm-0',
                                'align-items-center',
                                'plan-investment-notice'
                              ]}
                              componentTag="PDF"
                              fundName={plan.name}
                              fundTicker={plan.number}
                            />
                          ) : (
                            <ButtonIconLink
                              text="Quarterly Performance"
                              onClick={() => handleLinkClick(qiuPdfUrl)}
                              variant="Document"
                              textClass={'btn-icon'}
                              iconStyle={{
                                fontSize: '21px',
                                color: 'black',
                                'margin-right': '5px',
                                'margin-left': '3px',
                                top: '0'
                              }}
                              buttonClasses={[
                                'flex-nowrap',
                                'm-0',
                                'align-items-center',
                                'quarterly-performance'
                              ]}
                              componentTag="PDF"
                              fundName={plan.name}
                              fundTicker={plan.number}
                            />
                          );
                        }}
                      </DropdownBasic.Item>
                    </DropdownBasic>
                  ) : (
                    <div
                      {...className(
                        'd-flex',
                        'no-print',
                        'screener-plan-name-link',
                        isMobile ? 'px-4' : null
                      )}
                    >
                      {planNoticeUrl && (
                        <ButtonIconLink
                          text="Plan &amp; Investment Notice"
                          onClick={() => handleLinkClick(planNoticeUrl)}
                          variant="disclosureScreener"
                          textClass={'btn-icon--header'}
                          iconStyle={{ fontSize: '21px', color: 'white' }}
                          buttonClasses={[
                            'mt-0',
                            useRightMargin ? 'mr-7' : '',
                            'plan-investment-notice'
                          ]}
                          componentTag="PDF"
                          fundName={plan.name}
                          fundTicker={plan.number}
                        />
                      )}
                      <ButtonIconLink
                        text="Quarterly Performance"
                        onClick={() => handleLinkClick(qiuPdfUrl)}
                        variant="Document"
                        textClass={'btn-icon--header'}
                        iconStyle={{ fontSize: '21px', color: 'white' }}
                        buttonClasses={['mt-0', 'mr-0', 'quarterly-performance']}
                        componentTag="PDF"
                        fundName={plan.name}
                        fundTicker={plan.number}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </Col>
        </Row>
      )}
    </Container>
  );
};

PlanName.propTypes = {
  plans: PropTypes.array,
  updateScreenerPlanHoldings: PropTypes.func,
  participantHoldings: PropTypes.array,
  restorePlanHoldings: PropTypes.func
};

const mapStateToProps = state => {
  return {
    plans: state.filterReducer.planInfo,
    participantHoldings: state.filterReducer.participantHoldings
  };
};

const mapDispatchToProps = dispatch => {
  return {
    restorePlanHoldings: participantHoldings =>
      dispatch(restoreParticipantHoldings(participantHoldings)),
    updateScreenerPlanHoldings: (searchedPlanNumber, subPlan, channel, yyy) =>
      dispatch(updatePlanHoldings(searchedPlanNumber, subPlan, channel, yyy))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PlanName);
